import { useQuery, useMutation, useInfiniteQuery } from "@tanstack/react-query";
import type {
  UseQueryOptions,
  UseQueryResult,
  UseMutationResult,
  UseMutationOptions,
  UseInfiniteQueryOptions,
  InfiniteData,
} from "@tanstack/react-query";
import axios from "axios";
import type { AxiosRequestHeaders, InternalAxiosRequestConfig } from "axios";
import generateUrl from "../helpers/url";
import { toast } from "@toman/ui-kit-light";
import type { BaseResponse } from "../../types/api";
import { captureException, addBreadcrumb } from "../sentry/sentryConfig";

/**
 * @description Extended Axios config type that includes our custom _retry property
 */
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _showToast?: boolean;
}

/**
 * @description Enum for HTTP request methods
 */
export const RequestMethodType = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  PATCH: "PATCH",
  DELETE: "DELETE",
} as const;

/**
 * @description A pre-configured Axios instance that you can set up
 * interceptors or default headers on as needed.
 */
const axiosInstance = axios.create();

const GENERIC_ERROR_MESSAGE = "خطایی رخ داده است. لطفاً دوباره تلاش کنید.";
const NETWORK_ERROR_MESSAGE = "خطا در برقراری ارتباط با سرور. لطفاً اتصال اینترنت خود را بررسی کنید.";
const TOAST_EXCLUDED_MESSAGES = ["Invalid input.", "رمز عبور صحیح نیست."];

/**
 * @description Backend error bodies aren't always shaped the same way — some
 * carry `meta.error_message`, others only populate `meta.detail` (a DRF-style
 * string, or an object of field -> message(s), typically `non_field_errors`).
 * This pulls the best human-readable message out of whatever shape shows up.
 */
function extractDetailMessage(detail: unknown): string | undefined {
  if (!detail) return undefined;
  if (typeof detail === "string") return detail;
  if (typeof detail === "object") {
    const firstValue = Object.values(detail as Record<string, unknown>)[0];
    if (Array.isArray(firstValue) && firstValue.length) return String(firstValue[0]);
    if (typeof firstValue === "string") return firstValue;
  }
  return undefined;
}

function extractErrorMessage(
  body: Partial<BaseResponse<unknown>> | undefined,
  fallback: string,
): string {
  return body?.meta?.error_message || extractDetailMessage(body?.meta?.detail) || fallback;
}

function buildNormalizedError(
  body: Partial<BaseResponse<unknown>> | undefined,
  code: number,
  errorMessage: string,
): BaseResponse<unknown> {
  return {
    meta: {
      code,
      error_message: errorMessage,
      detail: body?.meta?.detail ?? errorMessage,
    },
    data: (body?.data ?? {}) as unknown,
    pagination: body?.pagination ?? { next_url: "", previous_url: "" },
  };
}

let refreshTokenPromise: Promise<{
  data: { token: string; refresh_token: string | null };
}> | null = null;

// Deduplicates concurrent 401s: all in-flight requests share a single refresh call
// rather than each triggering their own, which would exhaust the refresh token.
const refreshToken = async () => {
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = (async () => {
    try {
      const KeycloakService = (await import("../auth/keycloakService")).default;
      const keycloakService = KeycloakService.getInstance();

      const newToken = await keycloakService.refreshToken();
      if (!newToken) {
        addBreadcrumb("Token refresh failed - no token returned", "auth", "error");
        throw new Error("Refresh token failed");
      }

      addBreadcrumb("Token refreshed successfully", "auth", "info");
      return {
        data: {
          token: newToken,
          refresh_token: keycloakService.getRefreshToken(),
        },
      };
    } catch (error) {
      addBreadcrumb("Token refresh error", "auth", "error", {
        error: error instanceof Error ? error.message : String(error),
      });
      captureException(error, {
        tags: { error_type: "token_refresh_failed" },
        level: "error",
      });
      throw error;
    } finally {
      refreshTokenPromise = null;
    }
  })();

  return refreshTokenPromise;
};

const handleTokenRefresh = async (
  config: CustomAxiosRequestConfig,
  url?: string,
): Promise<InternalAxiosRequestConfig> => {
  if (config._retry) {
    throw new Error("Token refresh already attempted");
  }

  config._retry = true;
  addBreadcrumb("401 Unauthorized - attempting token refresh", "auth", "warning", {
    url: url || config.url,
  });

  try {
    const newToken = await refreshToken();
    config.headers.Authorization = `Bearer ${newToken.data.token}`;
    return config;
  } catch (refreshError) {
    captureException(refreshError, {
      tags: {
        error_type: "token_refresh_failed",
        http_status: "401",
        url: url || config.url || "unknown",
      },
      extra: {
        original_url: config.url,
        method: config.method,
      },
      level: "error",
    });
    throw refreshError;
  }
};

axiosInstance.interceptors.request.use(
  (config) => {
    addBreadcrumb(`API Request: ${config.method?.toUpperCase()} ${config.url}`, "http", "info", {
      method: config.method,
      url: config.url,
    });
    return config;
  },
  (error) => {
    captureException(error, {
      tags: { error_type: "api_request_error" },
      level: "error",
    });
    return Promise.reject(error);
  },
);

axiosInstance.interceptors.response.use(
  async (response) => {
    if (response.data?.meta?.code === 401) {
      try {
        const updatedConfig = await handleTokenRefresh(
          response.config as CustomAxiosRequestConfig,
          response.config.url,
        );
        return axiosInstance(updatedConfig);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    if (response.data?.meta?.code >= 400) {
      const config = response.config as CustomAxiosRequestConfig;
      const shouldShowToast = config._showToast !== false;

      const errorCode = response.data?.meta?.code;
      const errorMessage = extractErrorMessage(response.data, GENERIC_ERROR_MESSAGE);

      addBreadcrumb(`API Error: ${errorCode} - ${errorMessage}`, "http", "error", {
        status: errorCode,
        url: config.url,
        method: config.method,
        error_message: errorMessage,
      });

      if (!TOAST_EXCLUDED_MESSAGES.includes(errorMessage) && shouldShowToast) {
        toast.error(errorMessage);
      }

      if (errorCode >= 500) {
        captureException(new Error(`Backend Error: ${errorMessage}`), {
          tags: {
            error_type: "backend_error",
            http_status: String(errorCode),
            url: config.url || "unknown",
          },
          extra: {
            method: config.method,
            response_data: response.data,
            error_message: errorMessage,
          },
          level: "error",
        });
      }

      return Promise.reject(buildNormalizedError(response.data, errorCode, errorMessage));
    }

    addBreadcrumb(
      `API Success: ${response.status} ${response.config.method?.toUpperCase()} ${
        response.config.url
      }`,
      "http",
      "info",
      {
        status: response.status,
        url: response.config.url,
      },
    );

    return response;
  },
  async (error) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;

    if (error.response?.status === 401 && originalRequest) {
      try {
        const updatedConfig = await handleTokenRefresh(originalRequest, originalRequest.url);
        return axiosInstance(updatedConfig);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    const shouldShowToast = originalRequest?._showToast !== false;

    if (error.response) {
      const status = error.response.status;
      const url = error.config?.url || "unknown";
      const method = error.config?.method || "unknown";
      const errorMessage = extractErrorMessage(error.response.data, GENERIC_ERROR_MESSAGE);

      addBreadcrumb(
        `API Network Error: ${status} ${method.toUpperCase()} ${url}`,
        "http",
        "error",
        {
          status,
          url,
          method,
          response_data: error.response.data,
          error_message: errorMessage,
        },
      );

      if (status >= 500) {
        captureException(error, {
          tags: {
            error_type: "backend_error",
            http_status: String(status),
            url,
          },
          extra: {
            method,
            response_data: error.response.data,
            request_data: error.config?.data,
          },
          level: "error",
        });
      } else if (status >= 400 && status !== 401) {
        captureException(error, {
          tags: {
            error_type: "client_error",
            http_status: String(status),
            url,
          },
          extra: {
            method,
            response_data: error.response.data,
          },
          level: "warning",
        });
      }

      if (!TOAST_EXCLUDED_MESSAGES.includes(errorMessage) && shouldShowToast && status !== 401) {
        toast.error(errorMessage);
      }

      return Promise.reject(buildNormalizedError(error.response.data, status, errorMessage));
    } else if (error.request) {
      addBreadcrumb(
        `API Request Failed: No response received - ${error.config?.method?.toUpperCase()} ${
          error.config?.url
        }`,
        "http",
        "error",
        {
          url: error.config?.url,
          method: error.config?.method,
        },
      );

      captureException(error, {
        tags: {
          error_type: "network_error",
          url: error.config?.url || "unknown",
        },
        extra: {
          method: error.config?.method,
          message: error.message,
        },
        level: "error",
      });

      if (shouldShowToast) {
        toast.error(NETWORK_ERROR_MESSAGE);
      }

      return Promise.reject(buildNormalizedError(undefined, 0, NETWORK_ERROR_MESSAGE));
    } else {
      captureException(error, {
        tags: { error_type: "api_configuration_error" },
        level: "error",
      });

      if (shouldShowToast) {
        toast.error(GENERIC_ERROR_MESSAGE);
      }

      return Promise.reject(buildNormalizedError(undefined, 0, GENERIC_ERROR_MESSAGE));
    }
  },
);

/**
 * @description A minimal wrapper around Axios for a single request.
 * Returns the raw response data as type `T`.
 */
async function fetcher<T>(options: {
  method: (typeof RequestMethodType)[keyof typeof RequestMethodType];
  url: string;
  data?: unknown;
  headers?: AxiosRequestHeaders;
  showToast?: boolean;
}): Promise<T> {
  const KeycloakService = (await import("../auth/keycloakService")).default;
  const keycloakService = KeycloakService.getInstance();
  const token = keycloakService.getToken();

  const headers = {
    "Content-Type": "application/json",
    "Accept-Language": "fa",
    ...options.headers,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const requestConfig: CustomAxiosRequestConfig = {
    method: options.method,
    url: options.url,
    data: options.data,
    headers: headers as unknown as AxiosRequestHeaders,
    _showToast: options.showToast,
  };

  const { data } = await axiosInstance.request(requestConfig);

  return data as T;
}

/**
 * @description Options for the `useCustomQuery` hook, extending the
 * standard `UseQueryOptions` from React Query.
 */
interface UseCustomQueryOptions<TQueryFnData, TError = Error> extends Omit<
  UseQueryOptions<TQueryFnData, TError>,
  "queryKey" | "queryFn"
> {
  method?: (typeof RequestMethodType)[keyof typeof RequestMethodType];
  body?: Record<string, unknown>;
  params?: Record<string, unknown>;
  query?: Record<string, unknown>;
  headers?: AxiosRequestHeaders;
  showToast?: boolean;
}

/**
 * @description A hook for making an HTTP request to the specified URL.
 * It's similar to the built-in `useQuery` hook from React Query, but it
 * supports generating the URL based on the provided URL parameters and
 * query parameters.
 *
 * @param url The URL to make the request to.
 * @param options An object with the following properties:
 * - `method`: The HTTP method to use. Defaults to `GET`.
 * - `body`: The request body.
 * - `params`: An object with key-value pairs to be used as URL parameters.
 * - `queryParams`: An object with key-value pairs to be used as query
 *   parameters.
 * - `headers`: The request headers.
 * - `showToast`: Whether to show error toasts. Defaults to `true`.
 * @returns The result of calling the underlying `useQuery` hook.
 */
function useCustomQuery<TData = unknown, TError = Error>(
  url: string,
  {
    method = RequestMethodType.GET,
    body,
    params,
    query,
    headers,
    showToast = true,
    ...options
  }: UseCustomQueryOptions<TData, TError> = {},
): UseQueryResult<TData, TError> {
  const costructedUrl = generateUrl({ url, params, query });

  return useQuery<TData, TError>({
    queryKey: [costructedUrl, method, body],
    queryFn: () =>
      fetcher<TData>({
        method,
        url: costructedUrl,
        data: body,
        headers,
        showToast,
      }),
    ...options,
  });
}

/**
 * @description Options for the `useCustomMutation` hook, extending
 * the standard `UseMutationOptions` from React Query.
 */
interface UseCustomMutationOptions<TData, TError, TBody> extends Omit<
  UseMutationOptions<TData, TError, EffectiveMutationVariables<TBody>>,
  "mutationFn"
> {
  method?: (typeof RequestMethodType)[keyof typeof RequestMethodType];
  params?: Record<string, unknown>;
  queryParams?: Record<string, unknown>;
  useFormData?: boolean;
  showToast?: boolean;
}

type EffectiveMutationVariables<TBody> = TBody extends void
  ? { urlParams?: Record<string, unknown> } | void
  : TBody & { urlParams?: Record<string, unknown> };

/**
 * @description A hook for making a mutation request (POST, PUT, PATCH, DELETE) to the specified URL.
 * It's similar to the built-in `useMutation` hook from React Query, but it
 * supports generating the URL based on the provided URL parameters and
 * query parameters.
 * If you want to pass URL parameters dynamically during the mutation call, include them in the `variables` object under the `urlParams` key.
 * e.g., `mutate({ urlParams: { id: 1 }, ...otherBodyData })` or `mutate({ urlParams: { id: 1 } })` if no body is needed.
 * If `TBody` is `void` (the default), you can call `mutate()` without arguments if no dynamic `urlParams` are needed.
 *
 * @param url The URL to make the request to.
 * @param options An object with the following properties:
 * - `method`: The HTTP method to use. Defaults to `POST`.
 * - `body`: Default request body. Not typically used directly with mutations, pass data via `mutate`.
 * - `params`: Static URL parameters applied to all mutations.
 * - `queryParams`: Static query parameters applied to all mutations.
 * - `useFormData`: A boolean indicating whether to use form data for the request body. Defaults to `false`.
 * - `showToast`: Whether to show error toasts. Defaults to `true`.
 * @returns The result of calling the underlying `useMutation` hook.
 */
function useCustomMutation<TBody = void, TData = unknown, TError = BaseResponse<unknown>>(
  url: string,
  options?: UseCustomMutationOptions<TData, TError, TBody>,
): UseMutationResult<TData, TError, EffectiveMutationVariables<TBody>> {
  const {
    method = RequestMethodType.POST,
    params,
    queryParams,
    useFormData,
    showToast = true,
    ...mutationOptions
  } = options || {};

  return useMutation<TData, TError, EffectiveMutationVariables<TBody>>({
    mutationFn: async (variables: EffectiveMutationVariables<TBody>) => {
      let urlParams: Record<string, unknown> | undefined;
      let requestBody: TBody | undefined;

      if (typeof variables === "object" && variables !== null) {
        const { urlParams: dynamicParams, ...bodyContent } = variables as Record<string, unknown>;
        urlParams = dynamicParams as Record<string, unknown> | undefined;

        if (Object.keys(bodyContent).length > 0) {
          requestBody = bodyContent as TBody;
        } else {
          requestBody = undefined;
        }
      } else {
        urlParams = undefined;
        requestBody = undefined;
      }

      const constructedUrl = generateUrl({
        url,
        params: {
          ...params,
          ...urlParams,
        },
        query: queryParams,
      });

      const requestData =
        useFormData && requestBody
          ? Object.entries(requestBody).reduce((formData, [key, value]) => {
              if (value !== undefined) {
                if (value instanceof Blob) {
                  formData.append(key, value);
                } else {
                  formData.append(key, String(value));
                }
              }
              return formData;
            }, new FormData())
          : requestBody;

      return fetcher<TData>({
        method,
        url: constructedUrl,
        data: requestData,
        headers: {
          ...(requestData && useFormData && { "Content-Type": "multipart/form-data" }),
        } as AxiosRequestHeaders,
        showToast,
      });
    },
    ...mutationOptions,
  });
}

/**
 * @description Options for the `useCustomInfiniteQuery` hook, extending the
 * standard `UseInfiniteQueryOptions` from React Query.
 */
interface UseCustomInfiniteQueryOptions<TQueryFnData, TError = Error> extends Omit<
  UseInfiniteQueryOptions<TQueryFnData, TError, InfiniteData<TQueryFnData>>,
  "queryKey" | "queryFn"
> {
  method?: (typeof RequestMethodType)[keyof typeof RequestMethodType];
  body?: Record<string, unknown>;
  params?: Record<string, unknown>;
  query?: Record<string, unknown>;
  headers?: AxiosRequestHeaders;
  showToast?: boolean;
}

/**
 * @description A hook for making infinite queries with cursor-based pagination.
 * It's similar to the built-in `useInfiniteQuery` hook from React Query, but it
 * supports generating the URL based on the provided URL parameters and
 * query parameters, and handles cursor-based pagination automatically.
 *
 * @param url The URL to make the request to.
 * @param options An object with the following properties:
 * - `method`: The HTTP method to use. Defaults to `GET`.
 * - `body`: The request body.
 * - `params`: An object with key-value pairs to be used as URL parameters.
 * - `query`: An object with key-value pairs to be used as query parameters.
 * - `headers`: The request headers.
 * - `showToast`: Whether to show error toasts. Defaults to `true`.
 * @returns The result of calling the underlying `useInfiniteQuery` hook.
 */
function useCustomInfiniteQuery<TData = unknown, TError = Error>(
  url: string,
  {
    method = RequestMethodType.GET,
    body,
    params,
    query,
    headers,
    showToast = true,
    ...options
  }: UseCustomInfiniteQueryOptions<TData, TError>,
) {
  return useInfiniteQuery<TData, TError, InfiniteData<TData>>({
    queryKey: [url, method, body, params, query],
    queryFn: async ({ pageParam }) => {
      const constructedUrl =
        typeof pageParam === "string" && pageParam.includes("cursor")
          ? // TODO: fix this or prod
            pageParam.replace("http://", "https://")
          : generateUrl({ url, params, query });
      const response = await fetcher<TData>({
        method,
        url: constructedUrl,
        data: body,
        headers,
        showToast,
      });

      return response;
    },
    ...options,
  });
}

export { useCustomQuery, useCustomMutation, useCustomInfiniteQuery };
